import styled from 'styled-components';

import { colors, typography } from '../../styles/tokens';

const fontTitle = 72;
const fontBaseText = 21;

export const Container = styled.div`
  width: 1199px;
  margin: 40px auto;

  @media (max-width: 1199px) {
    width: calc(100% - 40px);
    margin: 0 auto;
  }
`;

export const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 40px auto 0;

  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid ${colors.borderAccent};
  background: ${colors.surfaceAlt};

  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.primaryHover};

  span {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: ${colors.primary};
    display: block;
  }
`;

export const Title = styled.h2`
  text-align: center;
  font-size: ${fontTitle}px;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: ${colors.textPrimary};

  margin: 24px 0 0;

  @media (max-width: 1199px) {
    font-size: ${fontTitle - 5}px;
  }
`;

export const Lead = styled.p`
  text-align: center;
  font-size: 19px;
  line-height: 1.65;
  color: ${colors.textSecondary};
  max-width: 720px;
  margin: 20px auto 40px;

  @media (max-width: 1199px) {
    font-size: 16px;
  }
`;

export const InfoCard = styled.div`
  border-radius: 30px;
  border: 1px solid ${colors.borderSubtle};
  background: ${colors.surfaceSunken};
  padding: clamp(26px, 3vw, 38px);
  margin: 0 0 22px;
`;

export const InfoCardTitle = styled.h3`
  font-family: ${typography.displayFontFamily};
  font-weight: 700;
  font-size: 24px;
  color: ${colors.textPrimary};
  margin: 0 0 8px;
`;

export const InfoCardText = styled.p`
  margin: 0 0 26px;
  font-size: 16px;
  line-height: 1.65;
  color: ${colors.textFaint};
  max-width: 640px;
`;

export const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
  gap: 20px;
`;

export const Step = styled.div`
  display: grid;
  gap: 10px;
  align-content: start;
`;

export const StepNumber = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: ${colors.surfaceAccent};
  color: ${colors.primary};
  display: grid;
  place-items: center;
  font-family: ${typography.displayFontFamily};
  font-weight: 700;
  font-size: 15px;
`;

export const StepTitle = styled.div`
  font-family: ${typography.displayFontFamily};
  font-weight: 600;
  font-size: 17px;
  color: ${colors.textPrimary};
`;

export const StepText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: ${colors.textFaint};
`;

export const ChecklistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 400px), 1fr));
  gap: 22px;
  margin-bottom: 22px;
`;

export const ChecklistCard = styled.div<{ muted?: boolean }>`
  border-radius: 30px;
  border: 1px solid ${colors.borderSubtle};
  background: ${props =>
    props.muted ? colors.surfaceAlt : colors.surfaceSunken};
  padding: clamp(26px, 3vw, 34px);
`;

export const ChecklistTitle = styled.h3`
  font-family: ${typography.displayFontFamily};
  font-weight: 700;
  font-size: 22px;
  margin: 0 0 20px;
  color: ${colors.textPrimary};
`;

export const ChecklistList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 12px;
`;

export const ChecklistItem = styled.li<{ muted?: boolean }>`
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 12px;
  font-size: 15px;
  line-height: 1.55;
  color: ${props => (props.muted ? colors.textFaint : colors.textSecondary)};

  span:first-child {
    color: ${props => (props.muted ? colors.textFaintest : colors.primary)};
  }
`;

export const WarningCallout = styled.div`
  border-radius: 24px;
  border-left: 3px solid ${colors.warning};
  background: ${colors.surfaceAlt};
  padding: 24px 28px;
  margin-bottom: 22px;
`;

export const WarningTitle = styled.div`
  font-family: ${typography.displayFontFamily};
  font-weight: 700;
  font-size: 17px;
  color: ${colors.warning};
  margin-bottom: 8px;
`;

export const WarningText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.65;
  color: ${colors.textSecondary};
  max-width: 760px;
`;

export const BaseText = styled.p`
  text-align: left;
  font-size: ${fontBaseText}px;
  font-weight: 400;
  color: ${colors.textSecondary};
  margin: 10px 0;

  a {
    color: ${colors.primary};

    &:hover {
      color: ${colors.primaryHover};
    }
  }

  @media (max-width: 1199px) {
    font-size: ${fontBaseText - 5}px;
  }
`;

export const Panel = styled.div`
  margin: 40px 0 80px;
  border-radius: 34px;
  border: 1px solid ${colors.border};
  background: ${colors.surfaceSunken};
  padding: 36px;

  @media (max-width: 1199px) {
    padding: 24px;
  }
`;

export const Form = styled.form`
  display: grid;
  gap: 20px;
`;

export const Field = styled.label`
  display: grid;
  gap: 8px;

  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.textFaint};

  input[type='email'],
  textarea {
    padding: 13px 16px;
    border-radius: 12px;
    border: 1px solid ${colors.borderAccent};
    background: ${colors.surface};
    color: ${colors.textPrimary};
    font-family: inherit;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;

    &:focus {
      border-color: ${colors.primary};
      outline: none;
    }
  }

  textarea {
    resize: vertical;
  }
`;

export const RadioGroup = styled.div`
  display: grid;
  gap: 12px;
`;

export const RadioOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  padding: 16px;
  border-radius: 14px;
  border: 1px solid ${colors.borderAccent};
  background: ${colors.surface};
  cursor: pointer;

  input {
    margin-top: 3px;
  }

  div {
    display: grid;
    gap: 4px;
  }

  strong {
    font-size: 15px;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0;
    color: ${colors.textPrimary};
  }

  span {
    font-size: 14px;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    color: ${colors.textSecondary};
  }
`;

export const SubmitButton = styled.button`
  padding: 15px 30px;
  border-radius: 999px;
  border: none;
  background: ${colors.primary};
  color: ${colors.onPrimary};
  font-family: ${typography.displayFontFamily};
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: background-color 0.2s;
  justify-self: start;

  &:hover:not(:disabled) {
    background: ${colors.primaryHover};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const ErrorText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${colors.danger};
`;

export const SuccessBox = styled.div`
  border: 1px solid ${colors.borderAccent};
  background: ${colors.surfaceAccent};
  border-radius: 18px;
  padding: 32px;

  div {
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: ${colors.primary};
    text-transform: uppercase;
  }

  p {
    margin: 12px 0 0;
    font-size: 16px;
    line-height: 1.6;
    color: ${colors.textPrimary};
  }
`;
